import {
  FORMAT_TYPES,
  ASSIST_FORMAT_TYPES,
  LINE_SEPARATOR
} from "../constants.js";

/**
 *  Serializer to convert json to delta
 *  Sample Input [{
      "type": "paragraph",
      "nodes": [
        {
          "type": "text",
          "text": "my"
        },
        {
          "type": "bold",
          "nodes": [
            {
              "type": "text",
              "text": "name"
            }
          ]
        },
        {
          "type": "text",
          "text": "is"
        },
        {
          "type": "bold",
          "nodes": [
            {
              "type": "italics",
              "nodes": [
                {
                  "type": "text",
                  "text": "vivek"
                }
              ]
            }
          ]
        }
      ]
    }]
    Sample Output {
      ops: [
        {
          insert: "my"
        },
        {
          insert: "name",
          attributes: {
            bold: true
          }
        },
        {
          insert: "is"
        },
        {
          insert: "vivek",
          attributes: {
            bold: true,
            italic: true
          }
        }
      ]
    }
*/

const INLINETYPES = [
  FORMAT_TYPES.BOLD,
  FORMAT_TYPES.ITALIC,
  FORMAT_TYPES.UNDERLINE,
  FORMAT_TYPES.STRIKE,
  FORMAT_TYPES.LINK,
  FORMAT_TYPES.COLOR,
  FORMAT_TYPES.SUBSCRIPT,
  FORMAT_TYPES.SUPSCRIPT
];

const BLOCKLEVELTYPES = [
  FORMAT_TYPES.H1,
  FORMAT_TYPES.H2,
  FORMAT_TYPES.H3,
  FORMAT_TYPES.OL_LIST,
  FORMAT_TYPES.UL_LIST,
  FORMAT_TYPES.BLOCKQUOTE,
  FORMAT_TYPES.CODE
];

function serializeNodes(nodes, obj, ops, shouldPush = true) {
  nodes &&
    nodes.length > 0 &&
    nodes.forEach((node) => {
      if (INLINETYPES.includes(node.type)) {
        obj.attributes = obj.attributes || {};
        if (node.type === FORMAT_TYPES.LINK) {
          obj.attributes[node.type] = node.data;
        } else if (node.data && node.data.align) {
          obj.attributes.align = node.data.align.toLowerCase();
        } else if (node.type === FORMAT_TYPES.COLOR) {
          obj.attributes[node.type] = node.data.color;
        } else {
          obj.attributes[node.type] = true;
        }
        serializeNodes(node.nodes, obj, ops, shouldPush);
      } else if (node.type === "text") {
        if (!shouldPush) return;
        // obj.insert = typeof obj.insert !== 'undefined' ? `${obj.insert}${node.text}` : node.text;
        if (node.text.split("\n").length > 1) {
          let arr = node.text.trim().split("\n");
          arr.forEach((text) => {
            ops.push(
              {
                insert: text,
                attributes: obj.attributes
                  ? Object.assign({}, obj.attributes)
                  : ""
              },
              {
                insert: LINE_SEPARATOR
              }
            );
          });
        } else {
          ops.push({
            insert: node.text,
            attributes: obj.attributes ? Object.assign({}, obj.attributes) : ""
          });
        }
        Reflect.deleteProperty(obj, "attributes");
        obj = Object.assign({});
      } else {
        obj = Object.assign({});
        obj.insert = {};
        obj.insert[node.type] = node.data;
        if (FORMAT_TYPES.IMAGE === node.type) {
          obj.insert[node.type] = node.data;
        }
        if (FORMAT_TYPES.VIDEO === node.type) {
          obj.insert[node.type] = node.data.url || node.data;
        }
        serializeNodes(node.nodes, obj, ops, false);
        ops.push(Object.assign({}, obj));
        Reflect.deleteProperty(obj, "attributes");
        obj = Object.assign({});
      }
    });
}

function serializeNestedList(node, insertObj, ops, type) {
  node &&
    node.forEach((item) => {
      if (item.type === "paragraph") {
        serializeNodes(item.nodes, insertObj, ops);
        ops.push({
          attributes: {
            list: ASSIST_FORMAT_TYPES[type],
            align:
              (item.data && item.data.align && item.data.align.toLowerCase()) ||
              ""
          },
          insert: "\n"
        });
      } else if (
        [FORMAT_TYPES.UL_LIST, FORMAT_TYPES.OL_LIST, "list_item"].includes(
          item.type
        )
      ) {
        serializeNestedList(item.nodes, insertObj, ops, type);
      }
    });
}

export function serializer(json = [], readOnly = false) {
  if (!json.length > 0) return "";
  let ops = [];
  json.forEach((item) => {
    let obj = {};
    if (BLOCKLEVELTYPES.includes(item.type)) {
      let blocktype = {};
      if (
        [FORMAT_TYPES.H1, FORMAT_TYPES.H2, FORMAT_TYPES.H3].includes(item.type)
      ) {
        blocktype = {
          header: ASSIST_FORMAT_TYPES[item.type],
          align:
            (item.data && item.data.align && item.data.align.toLowerCase()) ||
            ""
        };
        serializeNodes(item.nodes, obj, ops);
        ops.push({
          insert: "\n",
          attributes: blocktype
        });
      } else if (
        [
          FORMAT_TYPES.OL_LIST,
          FORMAT_TYPES.UL_LIST,
          FORMAT_TYPES.CODE,
          FORMAT_TYPES.BLOCKQUOTE
        ].includes(item.type)
      ) {
        item.nodes.forEach((node) => {
          let insertObj = {};
          if (
            [FORMAT_TYPES.CODE, FORMAT_TYPES.BLOCKQUOTE].includes(item.type)
          ) {
            serializeNodes(node.nodes, insertObj, ops);
            ops.push({
              attributes: {
                [item.type]: true,
                align:
                  (node.data &&
                    node.data.align &&
                    node.data.align.toLowerCase()) ||
                  ""
              },
              insert: "\n"
            });
          } else if (node.nodes?.length > 1) {
            /** serialization for nested lists */
            serializeNestedList([node], insertObj, ops, item.type);
          } else if (node.nodes?.length > 0) {
            let paragraphNodes = node.nodes[0];
            serializeNodes(paragraphNodes.nodes, insertObj, ops);
            ops.push({
              attributes: {
                list: ASSIST_FORMAT_TYPES[item.type],
                align:
                  (paragraphNodes.data &&
                    paragraphNodes.data.align &&
                    paragraphNodes.data.align.toLowerCase()) ||
                  ""
              },
              insert: "\n"
            });
          }
        });
      }
    } else if (item.type === "paragraph") {
      /** Check if code blocks exists inside paragraph */
      if (item.nodes[0] && item.nodes[0].type === FORMAT_TYPES.CODE) {
        item.nodes[0].nodes.forEach((node) => {
          let insert = {};
          serializeNodes(node.nodes, insert, ops);
          ops.push({
            attributes: {
              [FORMAT_TYPES.CODE]: true,
              align:
                (node.data &&
                  node.data.align &&
                  node.data.align.toLowerCase()) ||
                ""
            },
            insert: "\n"
          });
        });
      } else {
        serializeNodes(item.nodes, obj, ops);
      }
      if (item.data && item.data.align && item.data.align.toLowerCase()) {
        ops.push({
          attributes: {
            align:
              (item.data && item.data.align && item.data.align.toLowerCase()) ||
              ""
          },
          insert: "\n"
        });
      } else {
        ops.push({ insert: "\n" });
      }
    } else {
      /** serialization for custom components*/
      obj.insert = {};
      obj.insert[item.type] = item.data;
      ops.push(obj);
      if (!readOnly) ops.push({ insert: "\n" });
    }
  });
  return { ops };
}
